const CACHE = new Map<string, Intl.NumberFormat>()

/**
 * One `Intl.NumberFormat` per option set, built once and shared.
 *
 * Constructing one is expensive enough that a cell formatter must not do it per row. Keep
 * the cache a single Map for the segment — split it per formatter and the same option set
 * is built once for each of them.
 */
function numberFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options)
  let cached = CACHE.get(key)

  if (!cached) {
    cached = new Intl.NumberFormat('en-US', options)
    CACHE.set(key, cached)
  }

  return cached
}

/** Exactly `digits` decimals, trailing zeros kept, so a column of them lines up. */
export const fixed = (digits: number) =>
  numberFormat({ minimumFractionDigits: digits, maximumFractionDigits: digits })

/** At most `digits` decimals, trailing zeros dropped. */
export const upTo = (digits: number) =>
  numberFormat({ maximumFractionDigits: digits })

/** `393415` → `393.4K`. */
export const compact = () =>
  numberFormat({ notation: 'compact', maximumFractionDigits: 1 })

/**
 * Whether there is a figure to draw at all — every formatter in this segment opens with
 * `if (!hasFigure(value)) return EMPTY_CELL`.
 *
 * `null`, `undefined` and `NaN` draw as the empty cell and never as `0`: a metric the
 * backend had no base for is not zero, and a column that says `0` for it is read as a real
 * measurement.
 *
 * The predicate narrows the **true** branch, which is the one that goes on to format. `NaN`
 * is a `number` the compiler cannot tell apart, so it is ruled out here, not by a type.
 */
export function hasFigure(value: number | null | undefined): value is number {
  return value !== null && value !== undefined && !Number.isNaN(value)
}
